import api from './api';
import type { DateRangeDirection, DateRangeUnit } from '../utils/sampleDateRange';
import { resolveSampleDataSource } from './systems/sample-local/sampleLocalMode';
import { sampleLocalStore } from './systems/sample-local/sampleLocalStore';

/** クラウド DB → ローカル Zip 切替時の永久削除確認フレーズ（サーバーと一致必須） */
export const SAMPLE_LOCAL_SWITCH_PURGE_CONFIRM = 'DELETE CLOUD';

export type SampleLocalSwitchPurgePreview = {
  sampleCount: number;
  storageRoomCount: number;
  usageRecordCount: number;
};

export interface UsageRecord {
  id: number;
  sampleId: number;
  userId: number | null;
  quantity: number;
  usageDate: string;
  purpose?: string | null;
  notes?: string | null;
  action?: string | null;
  recordedBy?: string | null;
  remainingQuantity?: number | null;
  createdAt: string;
  user?: {
    id: number;
    username: string;
    firstName: string;
    lastName: string;
  };
}

export interface Sample {
  id: number;
  userId: number;
  storageRoomId?: number;
  name: string;
  sampleType?: string;
  bslLevel?: string;
  infectiousDiseaseLaw?: string;
  initialQuantity?: number;
  remainingQuantity?: number;
  unit?: string;
  storageDate?: string;
  expirationDate?: string;
  notes?: string;
  createdAt: string;
  updatedAt: string;
  usageRecords?: UsageRecord[];
  // 旧システム互換フィールド
  pathogenGenus?: string;
  pathogenSpecies?: string;
  pathogenSubspecies?: string;
  pathogenStrain?: string;
  pathogenNote?: string;
  livestockInfectiousDiseaseLaw?: string;
  drawerNumber?: number;
  drawerNote?: string;
  caseName?: string;
  createdBy?: string; // 暗号化されたユーザーID
  lastUsedDate?: string;
  /** 一部レスポンス互換用（snake_case） */
  last_used_date?: string;
  disposalDate?: string;
  creatorDisplayName?: string; // 表示用の作製者名
  /** 所有者ユーザー（詳細 API が含める場合） */
  user?: {
    id: number;
    username: string;
    firstName: string;
    lastName: string;
  };
  storageRoom?: {
    id: number;
    name: string;
    location: string | null;
    type?: string;
  };
}

export interface SampleListParams {
  page?: number;
  limit?: number;
  /** 全文検索（サンプル名・備考・病原体・保管庫・作製者など複数項目をOR検索） */
  search?: string;
  /** 詳細フィルター用：サンプル名のみで絞り込み */
  name?: string;
  storageRoomId?: number;
  sampleType?: string;
  bslLevel?: string;
  // 詳細フィルター（旧システム互換）
  preparer?: string;
  storageRoom?: string;
  drawerNumber?: string;
  caseName?: string;
  status?: string; // '利用可能' | '在庫なし' | '廃棄済み'
  pathogenGenus?: string;
  /** 作成日フィルター：中心日（YYYY-MM-DD） */
  createdDate?: string;
  /** 作成日の方向：前・後・前後（デフォルト前後） */
  createdDateDir?: DateRangeDirection;
  /** 作成日の数値（デフォルト0） */
  createdDateVal?: number;
  /** 作成日の単位：日・週・月・年（デフォルト日） */
  createdDateUnit?: DateRangeUnit;
  /** API用：作成日範囲の開始日（YYYY-MM-DD） */
  createdDateStart?: string;
  /** API用：作成日範囲の終了日（YYYY-MM-DD、inclusive） */
  createdDateEnd?: string;
  /** 最終使用日フィルター：中心日（YYYY-MM-DD） */
  lastUsedDate?: string;
  /** 最終使用日の方向：前・後・前後（デフォルト前後） */
  lastUsedDateDir?: DateRangeDirection;
  /** 最終使用日の数値（デフォルト0） */
  lastUsedDateVal?: number;
  /** 最終使用日の単位：日・週・月・年（デフォルト日） */
  lastUsedDateUnit?: DateRangeUnit;
  /** API用：最終使用日範囲の開始日（YYYY-MM-DD） */
  lastUsedDateStart?: string;
  /** API用：最終使用日範囲の終了日（YYYY-MM-DD、inclusive） */
  lastUsedDateEnd?: string;
  notes?: string;
  /** 備考が空のサンプルのみ */
  notesIsEmpty?: boolean;
  /** ケースセル完全一致フィルター */
  caseNameExact?: string;
  caseNameIsNull?: boolean;
  drawerNumberExact?: number;
  drawerNumberIsNull?: boolean;
  storageRoomIdIsNull?: boolean;
  /** ケース管理：共有保管庫内の他ユーザーサンプルも含める */
  includeSharedStorageSamples?: boolean;
  /** 管理者：全ユーザーの保管庫・サンプルを表示 */
  adminViewAll?: boolean;
}

export interface SampleListResponse {
  success: boolean;
  data: Sample[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
  stats?: {
    total: number;
    available: number;
    outOfStock: number;
    disposed: number;
  };
}

export interface LocationCaseSummary {
  caseName: string | null;
  sampleCount: number;
  totalRemaining: number | null;
}

export interface LocationDrawerSummary {
  drawerNumber: number | null;
  sampleCount: number;
  totalRemaining: number | null;
  cases: LocationCaseSummary[];
}

export interface LocationStorageRoomSummary {
  id: number;
  name: string;
  location: string | null;
  sampleCount: number;
  totalRemaining: number | null;
  isShared?: boolean;
  ownerUserId?: number | null;
  /** 保管庫オーナー（管理者の全ユーザー表示で同名保管庫を区別） */
  owner?: {
    firstName?: string | null;
    lastName?: string | null;
    username?: string | null;
  } | null;
  drawers: LocationDrawerSummary[];
}

export interface LocationWithoutStorageRoomSummary {
  sampleCount: number;
  totalRemaining: number | null;
  drawers: LocationDrawerSummary[];
}

export interface SampleLocationIndex {
  storageRooms: LocationStorageRoomSummary[];
  withoutStorageRoom: LocationWithoutStorageRoomSummary | null;
}

/** サンプル一覧・PDF用クエリパラメータを構築（undefined/null/空文字を除外、sampleIdsは配列展開） */
function buildSampleQueryParams(
  params: Record<string, unknown>,
  options?: { expandSampleIds?: boolean }
): Record<string, string | number> {
  const out: Record<string, string | number> = {};
  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') return;
    if (typeof value === 'boolean') {
      if (value) out[key] = 'true';
      return;
    }
    if (key === 'sampleIds' && Array.isArray(value) && options?.expandSampleIds) {
      value.forEach((id, i) => {
        out[`sampleIds[${i}]`] = id;
      });
    } else {
      out[key] = value as string | number;
    }
  });
  return out;
}

function adminViewAllRequestConfig(adminViewAll?: boolean) {
  return adminViewAll ? { params: { adminViewAll: 'true' } } : {};
}

export const sampleService = {
  async list(params: SampleListParams): Promise<SampleListResponse> {
    if (resolveSampleDataSource() === 'local') return sampleLocalStore.list(params);
    const response = await api.get<SampleListResponse>('/samples', {
      params: buildSampleQueryParams(params as Record<string, unknown>),
    });
    return response.data;
  },

  async getById(
    id: number,
    options?: { adminViewAll?: boolean }
  ): Promise<{ success: boolean; data: Sample }> {
    if (resolveSampleDataSource() === 'local') return sampleLocalStore.getById(id);
    const response = await api.get(`/samples/${id}`, {
      params: options?.adminViewAll ? { adminViewAll: true } : undefined,
    });
    return response.data;
  },

  async getLocationIndex(params?: {
    adminViewAll?: boolean;
  }): Promise<{ success: boolean; data: SampleLocationIndex }> {
    if (resolveSampleDataSource() === 'local') return sampleLocalStore.getLocationIndex();
    const response = await api.get<{ success: boolean; data: SampleLocationIndex }>(
      '/samples/location-index',
      { params: params?.adminViewAll ? { adminViewAll: true } : undefined }
    );
    return response.data;
  },

  async bulkDispose(
    sampleIds: number[],
    options?: { adminViewAll?: boolean }
  ): Promise<{ success: boolean; data: { disposed: number; sampleIds: number[] }; message: string }> {
    if (resolveSampleDataSource() === 'local') return sampleLocalStore.bulkDispose(sampleIds);
    const response = await api.post(
      '/samples/bulk-dispose',
      { sampleIds },
      adminViewAllRequestConfig(options?.adminViewAll)
    );
    return response.data;
  },

  async bulkDelete(
    sampleIds: number[],
    options?: { adminViewAll?: boolean }
  ): Promise<{ success: boolean; data: { deleted: number; sampleIds: number[] }; message: string }> {
    if (resolveSampleDataSource() === 'local') return sampleLocalStore.bulkDelete(sampleIds);
    const response = await api.post(
      '/samples/bulk-delete',
      { sampleIds },
      adminViewAllRequestConfig(options?.adminViewAll)
    );
    return response.data;
  },

  /** クラウド → ローカル切替前の削除件数（自分の所有分のみ・常に DB API） */
  async previewPurgeOwnedCloudForLocalSwitch(): Promise<{
    success: boolean;
    data: SampleLocalSwitchPurgePreview;
  }> {
    const response = await api.get('/samples/local-switch-purge/preview');
    return response.data;
  },

  /** クラウド → ローカル切替時の所有データ永久削除（常に DB API） */
  async purgeOwnedCloudForLocalSwitch(confirmText: string): Promise<{
    success: boolean;
    data: {
      deletedSamples: number;
      deletedStorageRooms: number;
      deletedUsageRecords: number;
    };
    message?: string;
  }> {
    const response = await api.post('/samples/local-switch-purge', { confirmText });
    return response.data;
  },

  async bulkAddUsage(
    records: Array<{
      sampleId: number;
      quantity: number;
      usageDate: string;
      purpose?: string;
      notes?: string;
      action?: string;
    }>,
    options?: { adminViewAll?: boolean }
  ): Promise<{ success: boolean; data: { count: number; sampleIds: number[] }; message: string }> {
    if (resolveSampleDataSource() === 'local') return sampleLocalStore.bulkAddUsage(records);
    const response = await api.post(
      '/samples/bulk-usage',
      { records },
      adminViewAllRequestConfig(options?.adminViewAll)
    );
    return response.data;
  },

  async create(
    data: Partial<Sample>,
    options?: { adminViewAll?: boolean }
  ): Promise<{ success: boolean; data: Sample }> {
    if (resolveSampleDataSource() === 'local') return sampleLocalStore.create(data);
    const response = await api.post('/samples', data, adminViewAllRequestConfig(options?.adminViewAll));
    return response.data;
  },

  async update(
    id: number,
    data: Partial<Sample>,
    options?: { adminViewAll?: boolean }
  ): Promise<{ success: boolean; data: Sample }> {
    if (resolveSampleDataSource() === 'local') return sampleLocalStore.update(id, data);
    const response = await api.put(`/samples/${id}`, data, adminViewAllRequestConfig(options?.adminViewAll));
    return response.data;
  },

  async delete(id: number, options?: { adminViewAll?: boolean }): Promise<{ success: boolean }> {
    if (resolveSampleDataSource() === 'local') return sampleLocalStore.delete(id);
    const response = await api.delete(`/samples/${id}`, adminViewAllRequestConfig(options?.adminViewAll));
    return response.data;
  },

  async addUsageRecord(
    sampleId: number,
    data: { quantity: number; usageDate: string; purpose?: string; notes?: string; action?: string },
    options?: { adminViewAll?: boolean }
  ): Promise<{ success: boolean; data: any }> {
    if (resolveSampleDataSource() === 'local') return sampleLocalStore.addUsageRecord(sampleId, data);
    const response = await api.post(
      `/samples/${sampleId}/usage`,
      data,
      adminViewAllRequestConfig(options?.adminViewAll)
    );
    return response.data;
  },

  async generateUsageRecordToken(sampleId: number): Promise<{ success: boolean; data: { token: string; url: string; sampleId: number } }> {
    // ローカル正本ではクラウド共有トークンを発行しない（既存 DB 行にも触れない）
    if (resolveSampleDataSource() === 'local') {
      throw new Error('sample.share_requires_login');
    }
    const response = await api.get(`/samples/generate-usage-record-token?sampleId=${sampleId}`);
    return response.data;
  },

  // パブリックアクセス用（認証不要）
  async getByEncryptedId(encryptedId: string): Promise<{ success: boolean; data: Sample }> {
    const response = await api.get(
      `/samples/encrypted/${encodeURIComponent(encryptedId)}`
    );
    return response.data;
  },

  async addUsageRecordByEncryptedId(
    encryptedId: string,
    data: { quantity: number; usageDate: string; purpose?: string; notes?: string; action?: string; recordedBy?: string }
  ): Promise<{ success: boolean; data: any }> {
    const response = await api.post(
      `/samples/encrypted/${encodeURIComponent(encryptedId)}/usage`,
      data
    );
    return response.data;
  },

  async generateEncryptedUrl(
    sampleId: number,
    options?: { adminViewAll?: boolean }
  ): Promise<{
    success: boolean;
    data: {
      sampleId: number;
      encryptedId: string;
      encryptedUrl: string;
      publicShareDisabled: boolean;
    };
  }> {
    if (resolveSampleDataSource() === 'local') {
      throw new Error('sample.share_requires_login');
    }
    const response = await api.get(`/samples/generate-encrypted-url?sampleId=${sampleId}`, {
      params: options?.adminViewAll ? { adminViewAll: 'true' } : undefined,
    });
    return response.data;
  },

  async generateLocationShareUrl(
    target: {
      scope: 'room' | 'drawer' | 'case';
      storageRoomId: number;
      drawerNumber?: number | null;
      caseName?: string | null;
    },
    options?: { adminViewAll?: boolean }
  ): Promise<{
    success: boolean;
    data: {
      scope: string;
      storageRoomId: number;
      encryptedId: string;
      encryptedUrl: string;
      roomName?: string;
      publicLocationShareDisabled?: boolean;
    };
  }> {
    if (resolveSampleDataSource() === 'local') {
      throw new Error('sample.share_requires_login');
    }
    const response = await api.get('/samples/generate-location-share-url', {
      params: {
        scope: target.scope,
        storageRoomId: target.storageRoomId,
        ...(target.scope !== 'room'
          ? {
              drawerNumber:
                target.drawerNumber == null || target.drawerNumber === undefined
                  ? ''
                  : String(target.drawerNumber),
            }
          : {}),
        ...(target.scope === 'case'
          ? { caseName: target.caseName == null ? '' : target.caseName }
          : {}),
        ...(options?.adminViewAll ? { adminViewAll: 'true' } : {}),
      },
    });
    return response.data;
  },

  async setPublicShareDisabled(
    sampleId: number,
    disabled: boolean,
    options?: { adminViewAll?: boolean }
  ): Promise<{
    success: boolean;
    data: { sampleId: number; publicShareDisabled: boolean };
  }> {
    if (resolveSampleDataSource() === 'local') {
      throw new Error('sample.share_requires_login');
    }
    const response = await api.patch(`/samples/${sampleId}/public-share`, { disabled }, {
      params: options?.adminViewAll ? { adminViewAll: 'true' } : undefined,
    });
    return response.data;
  },

  async setPublicLocationShareDisabled(
    storageRoomId: number,
    disabled: boolean,
    options?: { adminViewAll?: boolean }
  ): Promise<{
    success: boolean;
    data: { storageRoomId: number; publicLocationShareDisabled: boolean };
  }> {
    if (resolveSampleDataSource() === 'local') {
      throw new Error('sample.share_requires_login');
    }
    const response = await api.patch(
      `/samples/storage-rooms/${storageRoomId}/public-location-share`,
      { disabled },
      {
        params: options?.adminViewAll ? { adminViewAll: 'true' } : undefined,
      }
    );
    return response.data;
  },

  async getByLocationShareToken(token: string): Promise<{
    success: boolean;
    data: {
      scope: 'room' | 'drawer' | 'case';
      storageRoom: { id: number; name: string; location: string | null; type: string | null };
      drawerNumber: number | null;
      caseName: string | null;
      sampleCount: number;
      samples: Array<{
        id: number;
        name: string;
        sampleType: string | null;
        bslLevel: string | null;
        remainingQuantity: number | null;
        unit: string | null;
        disposalDate: string | null;
        drawerNumber: number | null;
        caseName: string | null;
        creatorDisplayName?: string;
        encryptedId: string | null;
        publicShareDisabled?: boolean;
      }>;
    };
  }> {
    const response = await api.get(`/samples/share/${encodeURIComponent(token)}`);
    return response.data;
  },

  async exportPdf(params?: SampleListParams & { sampleIds?: number[] }): Promise<Blob> {
    if (resolveSampleDataSource() === 'local') {
      throw new Error('sample.export_requires_db_mode');
    }
    const response = await api.get('/samples/export/pdf', {
      params: buildSampleQueryParams((params || {}) as Record<string, unknown>, { expandSampleIds: true }),
      responseType: 'blob',
    });
    return response.data;
  },
};

